import Button from '@mui/material/Button';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SxProps, Theme } from '@mui/material/styles';

type NavBackButtonProps = {
  fallbackPath?: string;
  parentPath?: string;
  className?: string;
  sx?: SxProps<Theme>;
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  iconOnly?: boolean;
};

export const NavBackButton = ({
  fallbackPath,
  parentPath,
  className,
  sx,
  size = 'small',
  onClick,
  iconOnly = false,
}: NavBackButtonProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const hasInternalHistory = location.key !== 'default';
  const isHashOnly =
    location.hash && location.pathname === window.location.pathname;

  const fallbackIsCurrentPage =
    fallbackPath !== undefined && fallbackPath === location.pathname;
  const parentIsCurrentPage =
    parentPath !== undefined && parentPath === location.pathname;

  const canGoBack =
    onClick ||
    hasInternalHistory ||
    (parentPath && !parentIsCurrentPage) ||
    (fallbackPath && !fallbackIsCurrentPage) ||
    (isHashOnly && parentPath);

  if (!canGoBack) {
    return null;
  }

  const handleBack = () => {
    if (onClick) {
      onClick();
      return;
    }

    // If we only changed hash → go to parentPath instead of history
    if (isHashOnly && parentPath) {
      navigate(parentPath);
      return;
    }

    // If we have real in-app history → go back
    if (hasInternalHistory) {
      navigate(-1);
      return;
    }

    // No history → use parentPath if available
    if (parentPath) {
      navigate(parentPath);
      return;
    }

    // Final fallback
    if (fallbackPath) {
      navigate(fallbackPath);
    }
  };

  return (
    <Button
      type="button"
      className={className}
      sx={sx}
      size={size}
      onClick={handleBack}
      aria-label={iconOnly ? 'Back' : undefined}
      startIcon={iconOnly ? undefined : <ArrowBack />}
    >
      {iconOnly ? <ArrowBack /> : 'Back'}
    </Button>
  );
};
